'use client'

import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'

// Major insurers (appear more frequently - weight 3)
const majorInsurers = [
  { name: 'BARMER', src: '/images/gkvs/Logo_BARMER_neu.png' },
  { name: 'Die Techniker', src: '/images/gkvs/Logo_Die_Techniker.png' },
  { name: 'DAK-Gesundheit', src: '/images/gkvs/Logo_DAK-Gesundheit.png' },
  {
    name: 'KKH',
    src: '/images/gkvs/Logo_KKH_Kaufmaennische_Krankenkasse_neu.png',
  },
  { name: 'AOK Bayern', src: '/images/gkvs/Logo_AOK_Bayern_bearbeitet.png' },

  { name: 'IKK classic', src: '/images/gkvs/Logo_IKK_classic.png' },
]

// Medium insurers (appear with normal frequency - weight 2)
const mediumInsurers = [
  { name: 'HEK', src: '/images/gkvs/Logo_HEK_Hanseatische_Krankenkasse.png' },
  { name: 'HKK', src: '/images/gkvs/Logo_HKK_Krankenkasse.png' },
  {
    name: 'AOK Rheinland/Hamburg',
    src: '/images/gkvs/Logo_AOK_Rheinland_Hamburg_bearbeitet.png',
  },
  {
    name: 'AOK PLUS',
    src: '/images/gkvs/Logo_AOK-PLUS-Die-Gesundheitskasse-fuer-Sachsen-und-Thueringen_bearbeitet.png',
  },
  { name: 'Knappschaft', src: '/images/gkvs/Logo_Knappschaft.png' },
  { name: 'IKK gesund plus', src: '/images/gkvs/Logo_IKK_gesund_plus.png' },
  { name: 'IKK Südwest', src: '/images/gkvs/Logo_IKK_suedwest.png' },
  {
    name: 'Mobil Krankenkasse',
    src: '/images/gkvs/Logo_Mobil_Krankenkasse.png',
  },
  { name: 'Pronova BKK', src: '/images/gkvs/Logo_Pronova_BKK.png' },
  { name: 'vivida BKK', src: '/images/gkvs/Logo_vivida_BKK.png' },
  { name: 'mhplus', src: '/images/gkvs/Logo_mhplus.png' },
  {
    name: 'Heimat Krankenkasse',
    src: '/images/gkvs/Logo_Heimat_Krankenkasse.png',
  },
]

// Smaller BKK insurers (appear less frequently - weight 1)
const smallerInsurers = [
  { name: 'Audi BKK', src: '/images/gkvs/Logo_Audi_BKK.png' },
  { name: 'BMW BKK', src: '/images/gkvs/Logo_BMW_BKK.png' },
  { name: 'Bosch BKK', src: '/images/gkvs/Logo_Bosch.png' },
  { name: 'Mercedes-Benz BKK', src: '/images/gkvs/Logo_Mercedes_Benz_BKK.png' },
  {
    name: 'Siemens BKK',
    src: '/images/gkvs/Logo_Siemens_Betriebskrankenkasse.png',
  },
  { name: 'BAHN-BKK', src: '/images/gkvs/Logo_BAHN-BKK.png' },
  { name: 'Debeka BKK', src: '/images/gkvs/Logo_Debeka_BKK.png' },
  { name: 'energie BKK', src: '/images/gkvs/Logo_energie_BKK.png' },
  { name: 'BKK VerbundPlus', src: '/images/gkvs/Logo_BKK_VerbundPlus.png' },
  { name: 'Salus BKK', src: '/images/gkvs/Logo_Salus-BKK.png' },
  { name: 'NOVITAS BKK', src: '/images/gkvs/Logo_NOVITAS_BKK.png' },
  { name: 'BKK Dachverband', src: '/images/gkvs/Logo_BKK_Dachverband.png' },
  { name: 'SECURVITA', src: '/images/gkvs/Logo_SECURVITA_KK.png' },
  { name: 'viactiv', src: '/images/gkvs/Logo_viactiv_Krankenkasse.png' },
  { name: 'BKK24', src: '/images/gkvs/Logo_BKK24.png' },
  { name: 'Bertelsmann BKK', src: '/images/gkvs/Logo_Bertelsmann_BKK.png' },
  {
    name: 'Continentale BKK',
    src: '/images/gkvs/Logo_Continentale_Betriebskrankenkasse.png',
  },
]

// Create weighted list
function createWeightedList() {
  const weighted: Array<{ name: string; src: string }> = []

  // Major insurers appear 3 times
  majorInsurers.forEach((insurer) => {
    weighted.push(insurer, insurer, insurer)
  })

  // Medium insurers appear 2 times
  mediumInsurers.forEach((insurer) => {
    weighted.push(insurer, insurer)
  })

  // Smaller insurers appear once
  smallerInsurers.forEach((insurer) => {
    weighted.push(insurer)
  })

  // Shuffle the array
  for (let i = weighted.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[weighted[i], weighted[j]] = [weighted[j], weighted[i]]
  }

  return weighted
}

// Get a deterministic but varied list for SSR
function getInitialLogos() {
  const all = [
    ...majorInsurers,
    ...majorInsurers,
    ...mediumInsurers,
    ...smallerInsurers.slice(0, 8),
  ]
  return all
}

export function GkvLogoCarousel() {
  const [logos] = useState(() => getInitialLogos())
  const scrollRef = useRef<HTMLDivElement>(null)
  const [isHovered, setIsHovered] = useState(false)

  useEffect(() => {
    const scrollContainer = scrollRef.current
    if (!scrollContainer) return

    let animationId: number
    let scrollPosition = 0
    const speed = 0.5 // pixels per frame

    const animate = () => {
      if (!isHovered) {
        scrollPosition += speed

        // Reset when we've scrolled half the content (seamless loop)
        const halfWidth = scrollContainer.scrollWidth / 2
        if (scrollPosition >= halfWidth) {
          scrollPosition = 0
        }

        scrollContainer.scrollLeft = scrollPosition
      }
      animationId = requestAnimationFrame(animate)
    }

    animationId = requestAnimationFrame(animate)

    return () => cancelAnimationFrame(animationId)
  }, [isHovered])

  // Double the logos for seamless infinite scroll
  const displayLogos = [...logos, ...logos]

  return (
    <div className="relative w-full overflow-hidden">
      {/* Gradient masks for fade effect */}
      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-white to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-white to-transparent" />

      <div
        ref={scrollRef}
        className="flex gap-8 overflow-hidden py-4"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {displayLogos.map((logo, index) => (
          <div
            key={`${logo.name}-${index}`}
            className="flex h-12 w-24 flex-shrink-0 items-center justify-center grayscale transition-all duration-300 hover:grayscale-0"
          >
            <Image
              src={logo.src}
              alt={logo.name}
              width={96}
              height={48}
              className="h-auto max-h-12 w-auto max-w-24 object-contain"
              unoptimized
            />
          </div>
        ))}
      </div>
    </div>
  )
}

// Draggable marquee carousel with touch and mouse support
export function GkvLogoMarquee() {
  const logos = getInitialLogos()
  const containerRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)

  // State for drag/touch interaction
  const [isDragging, setIsDragging] = useState(false)
  const dragState = useRef({
    startX: 0,
    scrollLeft: 0,
    velocity: 0,
    lastX: 0,
    lastTime: 0,
    animationPaused: false,
  })

  // Position state managed by ref for performance
  const positionRef = useRef(0)
  const autoScrollSpeed = 0.5

  // Auto-scroll animation
  useEffect(() => {
    const track = trackRef.current
    if (!track) return

    let animationId: number
    let lastTimestamp = 0

    const animate = (timestamp: number) => {
      if (!lastTimestamp) lastTimestamp = timestamp
      const delta = timestamp - lastTimestamp
      lastTimestamp = timestamp

      if (!dragState.current.animationPaused) {
        // Apply velocity decay when not dragging (momentum)
        if (Math.abs(dragState.current.velocity) > 0.1) {
          positionRef.current += dragState.current.velocity
          dragState.current.velocity *= 0.95 // Friction
        } else {
          // Normal auto-scroll
          dragState.current.velocity = 0
          positionRef.current += autoScrollSpeed
        }

        // Loop seamlessly
        const halfWidth = track.scrollWidth / 2
        if (positionRef.current >= halfWidth) {
          positionRef.current -= halfWidth
        } else if (positionRef.current < 0) {
          positionRef.current += halfWidth
        }

        track.style.transform = `translateX(${-positionRef.current}px)`
      }

      animationId = requestAnimationFrame(animate)
    }

    animationId = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(animationId)
  }, [])

  // Mouse event handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true)
    dragState.current.animationPaused = true
    dragState.current.startX = e.clientX
    dragState.current.scrollLeft = positionRef.current
    dragState.current.lastX = e.clientX
    dragState.current.lastTime = Date.now()
    dragState.current.velocity = 0
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return
    e.preventDefault()

    const x = e.clientX
    const walk = dragState.current.startX - x
    positionRef.current = dragState.current.scrollLeft + walk

    // Calculate velocity for momentum
    const now = Date.now()
    const dt = now - dragState.current.lastTime
    if (dt > 0) {
      dragState.current.velocity = ((dragState.current.lastX - x) / dt) * 16
    }
    dragState.current.lastX = x
    dragState.current.lastTime = now

    // Wrap position for seamless looping
    if (trackRef.current) {
      const halfWidth = trackRef.current.scrollWidth / 2
      if (positionRef.current >= halfWidth) {
        positionRef.current -= halfWidth
      } else if (positionRef.current < 0) {
        positionRef.current += halfWidth
      }
      trackRef.current.style.transform = `translateX(${-positionRef.current}px)`
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
    // Resume auto-scroll after a delay
    setTimeout(() => {
      dragState.current.animationPaused = false
    }, 2000)
  }

  const handleMouseLeave = () => {
    if (isDragging) {
      handleMouseUp()
    }
  }

  // Touch event handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true)
    dragState.current.animationPaused = true
    dragState.current.startX = e.touches[0].clientX
    dragState.current.scrollLeft = positionRef.current
    dragState.current.lastX = e.touches[0].clientX
    dragState.current.lastTime = Date.now()
    dragState.current.velocity = 0
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return

    const x = e.touches[0].clientX
    const walk = dragState.current.startX - x
    positionRef.current = dragState.current.scrollLeft + walk

    // Calculate velocity for momentum
    const now = Date.now()
    const dt = now - dragState.current.lastTime
    if (dt > 0) {
      dragState.current.velocity = ((dragState.current.lastX - x) / dt) * 16
    }
    dragState.current.lastX = x
    dragState.current.lastTime = now

    // Wrap position for seamless looping
    if (trackRef.current) {
      const halfWidth = trackRef.current.scrollWidth / 2
      if (positionRef.current >= halfWidth) {
        positionRef.current -= halfWidth
      } else if (positionRef.current < 0) {
        positionRef.current += halfWidth
      }
      trackRef.current.style.transform = `translateX(${-positionRef.current}px)`
    }
  }

  const handleTouchEnd = () => {
    setIsDragging(false)
    // Resume auto-scroll after a delay
    setTimeout(() => {
      dragState.current.animationPaused = false
    }, 2000)
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
    >
      {/* Gradient masks */}
      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-white to-transparent sm:w-20" />
      <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-white to-transparent sm:w-20" />

      <div
        ref={trackRef}
        className="flex select-none"
        style={{ willChange: 'transform' }}
      >
        {[...logos, ...logos].map((logo, index) => (
          <div
            key={`${logo.name}-${index}`}
            className="mx-4 flex h-10 w-20 flex-shrink-0 items-center justify-center opacity-60 grayscale transition-opacity duration-300 hover:opacity-100 hover:grayscale-0 sm:mx-6 sm:h-12 sm:w-24"
          >
            <Image
              src={logo.src}
              alt={logo.name}
              width={96}
              height={48}
              className="pointer-events-none h-auto max-h-10 w-auto max-w-20 object-contain sm:max-h-12 sm:max-w-24"
              draggable={false}
              unoptimized
            />
          </div>
        ))}
      </div>
    </div>
  )
}
